import type { EditorialPillar } from './editorial-chain';

/**
 * The taxonomy of a workspace's editorial map: pillars and their branches, read from
 * `brand_kits.brand_guidelines.pillars[]`. Nothing stores a pillar's NAME on a topic (`project_tasks.pillar_id`
 * and `branch_id` are text keys into this jsonb), so every surface that files a topic (the map, the dock's
 * "Refile topic", and the article import) validates against this one reader.
 */

interface RawPillar {
  id?: unknown;
  name?: unknown;
  weight?: unknown;
  branches?: unknown;
}

export function readPillars(guidelines: unknown): EditorialPillar[] {
  const g = (guidelines ?? {}) as { pillars?: unknown };
  if (!Array.isArray(g.pillars)) return [];
  return g.pillars.flatMap((raw): EditorialPillar[] => {
    const p = raw as RawPillar;
    if (typeof p.id !== 'string' || !p.id || typeof p.name !== 'string' || !p.name) return [];
    const branches = Array.isArray(p.branches)
      ? p.branches.flatMap((b) => {
          const branch = b as { id?: unknown; name?: unknown };
          return typeof branch.id === 'string' &&
            branch.id &&
            typeof branch.name === 'string' &&
            branch.name
            ? [{ id: branch.id, name: branch.name }]
            : [];
        })
      : [];
    return [
      {
        id: p.id,
        name: p.name,
        weight: typeof p.weight === 'number' ? p.weight : 0,
        branches,
      },
    ];
  });
}

export interface RefileRequest {
  pillarId?: string | null;
  branchId?: string | null;
}

/** Flat on purpose: this project does not narrow unions. `error` is empty when the request is valid. */
export interface RefilePlan {
  error: string;
  pillarId: string | null;
  branchId: string | null;
}

/**
 * A pillar and branch to file a topic under, checked against the workspace's own taxonomy. A branch belongs
 * to one pillar, so it is only valid beside that pillar. A pillar given without a branch files the topic to
 * the pillar alone, the way the dock's "No branch" does.
 */
export function planRefile(
  pillars: readonly EditorialPillar[],
  request: RefileRequest,
): RefilePlan {
  const pillarId = request.pillarId?.trim() || null;
  const branchId = request.branchId?.trim() || null;
  if (!pillarId && !branchId) return { error: '', pillarId: null, branchId: null };
  if (!pillarId)
    return {
      error: 'A branch can only be set together with its pillar.',
      pillarId: null,
      branchId: null,
    };
  const pillar = pillars.find((candidate) => candidate.id === pillarId);
  if (!pillar)
    return {
      error: `Pillar "${pillarId}" is not in this workspace's content pillars (${
        pillars.map((candidate) => `${candidate.id} = ${candidate.name}`).join('; ') ||
        'none defined'
      }).`,
      pillarId: null,
      branchId: null,
    };
  if (branchId && !pillar.branches.some((branch) => branch.id === branchId))
    return {
      error: `Branch "${branchId}" is not under pillar "${pillar.name}" (${
        pillar.branches.map((branch) => `${branch.id} = ${branch.name}`).join('; ') || 'no branches'
      }).`,
      pillarId: null,
      branchId: null,
    };
  return { error: '', pillarId: pillar.id, branchId };
}
